import config from "@/config/config";
import { Currencies } from "@/store/currencyStore";
import { AxiosError } from "axios";
const RTLanguages = ["ar"];

export const isRTLLanguage = (lang: string) => {
  return RTLanguages.includes(lang);
};

export const bgOverlayStyle = (
  imgUrl?: string,
) => `linear-gradient(0deg, rgba(0, 0, 0, 0) 56.92%, rgba(0, 0, 0, 0.3) 68.22%, rgba(0, 0, 0, 0.4) 100%),
linear-gradient(180deg, rgba(0, 0, 0, 0) 40.39%, rgba(0, 0, 0, 0.3) 61.28%, rgba(0, 0, 0, 0.4) 92.02%),
linear-gradient(220deg, rgba(0, 0, 0, 0) 40.39%, rgba(0, 0, 0, 0.3) 61.28%, rgba(0, 0, 0, 0.4) 92.02%),
linear-gradient(30deg, rgba(0, 0, 0, 0) 40.39%, rgba(0, 0, 0, 0.3) 61.28%, rgba(0, 0, 0, 0.4) 92.02%), url(${imgUrl})`;

export const paramsToKey = (params?: Record<string, string>) => {
  if (params) {
    return Object.entries(params).map(([k, v]) => `${k}:${v}`);
  } else {
    return [];
  }
};

export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  exchangeRate: number,
): number => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  if (fromCurrency === Currencies.USD && toCurrency === Currencies.EGP) {
    return amount * exchangeRate;
  }

  if (fromCurrency === Currencies.EGP && toCurrency === Currencies.USD) {
    return amount / exchangeRate;
  }

  return amount;
};

export const toggleElementFromArray = (arr: string[], item: string) => {
  if (item === "") {
    return [""];
  }
  return arr.includes(item)
    ? arr.filter((i) => i !== item)
    : [...arr, item].filter((i) => i !== "");
};

export const getCookieDomain = () => {
  return config.prod ? "." + window.location.hostname.split(".").slice(1).join(".") : "localhost";
};

export const getPageNumberFromURL = (url?: string | null) => {
  if (!url) {
    return undefined;
  }
  const parsed = new URL(url);
  return parsed.searchParams.get("page");
};

/**
 * Checks if a string contains Arabic characters
 * @param text - The text to check
 * @returns boolean - True if the text contains Arabic characters
 */
export const containsArabic = (text?: string): boolean => {
  if (!text) {
    return false;
  }

  // Arabic Unicode ranges
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  return arabicPattern.test(text);
};

export function isLocalDevelopment() {
  return (
    process.env.NODE_ENV === "development" &&
    (location.hostname === "localhost" || location.hostname === "127.0.0.1")
  );
}

export function parseAPIError(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (data && typeof data === "object" && "error" in data) {
      return Object.values(data.error as Record<string, string[]>).flat();
    }
    return [error.message];
  }
  if (error instanceof Error) {
    return [error.message];
  }

  return ["Something went wrong"];
}

/**
 * Parses application submission errors with specific handling for:
 * - 400 errors: non_field_errors or field-specific errors
 * - 429 errors: rate limiting with retry time
 * @param error - The error object from the API call
 * @returns Array of error messages to display
 */
export function parseApplicationSubmissionError(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data;

    // Handle 429 Too Many Requests
    if (status === 429) {
      let retryAfterSeconds: number | null = null;

      // Try to get retry time from Retry-After header
      const retryAfterHeader = error.response?.headers?.["retry-after"];
      if (retryAfterHeader) {
        retryAfterSeconds = parseInt(retryAfterHeader, 10);
      }

      // Try to get retry time from response body
      if (!retryAfterSeconds && data) {
        if (typeof data === "object") {
          // Check common field names for retry time
          const possibleFields = ["retry_after", "retryAfter", "wait_time", "waitTime", "seconds"];
          for (const field of possibleFields) {
            if (field in data) {
              const value = (data as any)[field];
              if (typeof value === "number") {
                retryAfterSeconds = value;
                break;
              } else if (typeof value === "string") {
                const parsed = parseInt(value, 10);
                if (!isNaN(parsed)) {
                  retryAfterSeconds = parsed;
                  break;
                }
              }
            }
          }
        } else if (typeof data === "number") {
          // If the response data itself is a number (seconds)
          retryAfterSeconds = data;
        }
      }

      // Format the retry message
      if (retryAfterSeconds && !isNaN(retryAfterSeconds)) {
        const minutes = Math.floor(retryAfterSeconds / 60);
        const seconds = retryAfterSeconds % 60;

        if (minutes > 0) {
          return [
            `Too many requests. Please try again later in ${minutes} ${minutes === 1 ? "minute" : "minutes"}${seconds > 0 ? ` and ${seconds} ${seconds === 1 ? "second" : "seconds"}` : ""}.`,
          ];
        } else {
          return [
            `Too many requests. Please try again later in ${retryAfterSeconds} ${retryAfterSeconds === 1 ? "second" : "seconds"}.`,
          ];
        }
      } else {
        return ["Too many requests. Please try again later."];
      }
    }

    // Handle 400 Bad Request
    if (status === 400 && data && typeof data === "object") {
      const errors: string[] = [];

      // Check for non_field_errors first
      if ("non_field_errors" in data && Array.isArray(data.non_field_errors)) {
        errors.push(...data.non_field_errors);
      }

      // Check for field-specific errors
      const fieldErrors = ["name", "email", "phone_number", "linkedin", "cv"] as const;
      fieldErrors.forEach((field) => {
        if (field in data && Array.isArray(data[field])) {
          errors.push(...data[field]);
        }
      });

      if (errors.length > 0) {
        return errors;
      }
    }

    // Fallback to default error message
    return [error.message || "Something went wrong"];
  }

  if (error instanceof Error) {
    return [error.message];
  }

  return ["Something went wrong"];
}
